import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Select, Button, Alert, Card, Modal, Form, Input, InputNumber, message, Drawer, Descriptions, Radio, Divider, Space, AutoComplete, Upload } from 'antd';
import { PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, WarningOutlined, UploadOutlined } from '@ant-design/icons';
import { getProjects, getMIRs, createMIR, submitMIRForReview, submitMIRResult, getMIRMaterialTypes, getMIRChecklist, getUsers, getWorkflowObjects } from '../services/api';
import { getApiError } from '../utils/apiError';
import { useNavigation } from '../context/NavigationContext';
import RecordTimeline from '../components/RecordTimeline';
import StagePhotos from '../components/StagePhotos';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Project {
  id: string;
  code: string;
  name: string;
}

interface ChecklistItem {
  item_name: string;
  expected_criteria: string | null;
  result: string | null;
  remarks: string;
  adHoc?: boolean;
}

const MIRPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [mirs, setMirs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [materialTypes, setMaterialTypes] = useState<string[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMIR, setSelectedMIR] = useState<any>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [overallResult, setOverallResult] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submittingResult, setSubmittingResult] = useState(false);

  const [users, setUsers] = useState<any[]>([]);
  const [reviewerId, setReviewerId] = useState<string>('');
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [reviewEvidence, setReviewEvidence] = useState<File[]>([]);
  const [resultEvidence, setResultEvidence] = useState<File[]>([]);

  useEffect(() => {
    loadProjects();
    loadMaterialTypes();
    loadUsers();
  }, []);

  const { pendingTarget, clearPendingTarget } = useNavigation();
  const target = pendingTarget?.recordType === 'MIR' ? pendingTarget : null;

  // Opens the record that brought the user here through a clickable ID, once the list has loaded.
  useEffect(() => {
    if (!target || loading) return;
    const match = mirs.find((m) => m.workflow?.object_id === target.recordId);
    if (match) {
      openDrawer(match);
      clearPendingTarget();
    }
  }, [mirs, loading, target, clearPendingTarget]);

  // A pending target only belongs to the visit it was set for.
  useEffect(() => clearPendingTarget, [clearPendingTarget]);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        const initial = response.data.find((p: any) => p.id === target?.projectId) || response.data[0];
        setSelectedProject(initial.id);
        loadMIRs(initial.id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadMaterialTypes = async () => {
    try {
      const res = await getMIRMaterialTypes();
      setMaterialTypes(res.data.material_types || []);
    } catch (err) {
      // Non-blocking
    }
  };

  const loadUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data || []);
    } catch (err) {
      // Non-blocking
    }
  };

  const loadMIRs = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getMIRs(projectId);
      const workflowResponse = await getWorkflowObjects(projectId, { type: 'MIR' });
      const workflowMap: { [key: string]: any } = {};
      workflowResponse.data.forEach((wo: any) => {
        workflowMap[wo.id] = wo;
      });
      const enrichedMIRs = response.data.map((mir: any) => ({
        ...mir,
        workflow: workflowMap[mir.workflow_object] || {},
      }));
      setMirs(enrichedMIRs);
    } catch (err) {
      setError('Failed to load MIRs.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadMIRs(value);
  };

  const openDrawer = (record: any) => {
    setSelectedMIR(record);
    setChecklist(record.checklist_results && record.checklist_results.length > 0 ? record.checklist_results : []);
    setOverallResult(record.overall_result === 'PENDING' ? '' : record.overall_result);
    setRejectionReason(record.rejection_reason || '');
    setReviewerId('');
    setReviewEvidence([]);
    setResultEvidence([]);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedMIR(null);
    setChecklist([]);
    setOverallResult('');
    setRejectionReason('');
    setReviewerId('');
    setReviewEvidence([]);
    setResultEvidence([]);
  };

  const updateChecklistItem = (index: number, field: 'item_name' | 'result' | 'remarks', value: string) => {
    const updated = [...checklist];
    updated[index] = { ...updated[index], [field]: value };
    setChecklist(updated);
  };

  const addAdHocItem = () => {
    setChecklist([...checklist, { item_name: '', expected_criteria: null, result: null, remarks: '', adHoc: true }]);
  };

  const handleSubmitForReview = async () => {
    if (!selectedMIR) return;
    if (!reviewerId) {
      message.error('Please select a reviewer.');
      return;
    }
    if (reviewEvidence.length === 0) {
      message.error('Please attach at least one inspection evidence photo.');
      return;
    }
    setSubmittingForReview(true);
    try {
      const formData = new FormData();
      formData.append('checklist_results', JSON.stringify(checklist));
      formData.append('reviewer_id', reviewerId);
      reviewEvidence.forEach((file) => formData.append('evidence', file));
      await submitMIRForReview(selectedMIR.id, formData);
      message.success('Submitted for review.');
      closeDrawer();
      loadMIRs(selectedProject);
    } catch (err: any) {
      message.error(getApiError(err, 'Failed to submit for review.'), 8);
    } finally {
      setSubmittingForReview(false);
    }
  };

  const handleSubmitResult = async () => {
    if (!selectedMIR) return;
    if (!overallResult) {
      message.error('Please select an overall result.');
      return;
    }
    if (overallResult === 'REJECTED' && !rejectionReason.trim()) {
      message.error('Rejection reason is mandatory when rejecting.');
      return;
    }
    if (resultEvidence.length === 0) {
      message.error('Please attach at least one evidence photo.');
      return;
    }
    setSubmittingResult(true);
    try {
      const formData = new FormData();
      formData.append('checklist_results', JSON.stringify(checklist));
      formData.append('overall_result', overallResult);
      if (overallResult === 'REJECTED') {
        formData.append('rejection_reason', rejectionReason);
      }
      resultEvidence.forEach((file) => formData.append('evidence', file));
      await submitMIRResult(selectedMIR.id, formData);
      message.success('Inspection result submitted.');
      closeDrawer();
      loadMIRs(selectedProject);
    } catch (err: any) {
      message.error(getApiError(err, 'Failed to submit result.'), 8);
    } finally {
      setSubmittingResult(false);
    }
  };

  const openModal = () => {
    form.resetFields();
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleMaterialTypeSelect = async (materialType: string) => {
    try {
      const res = await getMIRChecklist(materialType);
      form.setFieldsValue({ _checklistPreview: res.data.checklist || [] });
    } catch (err) {
      // Non-blocking
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        material_type: values.material_type,
        supplier_name: values.supplier_name,
        batch_lot_number: values.batch_lot_number,
        quantity: values.quantity,
        quantity_unit: values.quantity_unit,
      };
      await createMIR(selectedProject, payload);
      message.success('MIR created successfully.');
      setModalOpen(false);
      loadMIRs(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const apiMessage = err?.response?.data?.detail || err?.response?.data?.material_type?.[0] || 'Failed to create MIR.';
      message.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'MIR ID',
      dataIndex: ['workflow', 'object_id'],
      key: 'object_id',
      render: (text: string) => <Text strong style={{ color: '#1B2A4A' }}>{text}</Text>,
    },
    {
      title: 'Material Type',
      dataIndex: 'material_type',
      key: 'material_type',
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      render: (text: string) => text || '—',
    },
    {
      title: 'Batch/Lot',
      dataIndex: 'batch_lot_number',
      key: 'batch_lot_number',
      render: (text: string) => text || '—',
    },
    {
      title: 'Status',
      dataIndex: ['workflow', 'status'],
      key: 'wf_status',
      render: (statusVal: string) => <Tag>{statusVal?.replace('_', ' ') || 'CREATED'}</Tag>,
    },
    {
      title: 'Result',
      dataIndex: 'overall_result',
      key: 'overall_result',
      render: (result: string) => {
        if (result === 'PENDING') return <Tag icon={<ClockCircleOutlined />} color="orange">Pending</Tag>;
        if (result === 'ACCEPTED') return <Tag icon={<CheckCircleOutlined />} color="green">Accepted</Tag>;
        if (result === 'ACCEPTED_WITH_CONDITIONS') return <Tag icon={<WarningOutlined />} color="gold">Accepted w/ Conditions</Tag>;
        if (result === 'REJECTED') return <Tag icon={<CloseCircleOutlined />} color="red">Rejected</Tag>;
        return <Tag>{result}</Tag>;
      },
    },
    {
      title: 'Inspected On',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          MIR Register
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openModal} disabled={!selectedProject}>
            Raise MIR
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={mirs}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No MIRs found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Drawer
        title={selectedMIR?.workflow?.object_id || 'MIR Details'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={560}
      >
        {selectedMIR && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Material Type">{selectedMIR.material_type}</Descriptions.Item>
              <Descriptions.Item label="Supplier">{selectedMIR.supplier_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Batch/Lot Number">{selectedMIR.batch_lot_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="Quantity">
                {selectedMIR.quantity ? `${selectedMIR.quantity} ${selectedMIR.quantity_unit || ''}` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag>{selectedMIR.workflow?.status?.replace('_', ' ') || 'CREATED'}</Tag>
              </Descriptions.Item>
            </Descriptions>

            <Divider>Checklist</Divider>

            {checklist.length === 0 ? (
              <Text type="secondary">No checklist items. Use "Add Item" below if inspecting without a template.</Text>
            ) : (
              checklist.map((item, index) => (
                <Card key={index} size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
                  {item.item_name && !item.adHoc ? (
                    <Text strong>{item.item_name}</Text>
                  ) : (
                    <Input
                      placeholder="Ad-hoc item name"
                      value={item.item_name}
                      onChange={(e) => updateChecklistItem(index, 'item_name', e.target.value)}
                      style={{ marginBottom: 8 }}
                    />
                  )}
                  {item.expected_criteria && (
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{item.expected_criteria}</div>
                  )}
                  <Radio.Group
                    value={item.result}
                    onChange={(e) => updateChecklistItem(index, 'result', e.target.value)}
                    style={{ marginBottom: 8 }}
                  >
                    <Radio value="PASS">Pass</Radio>
                    <Radio value="FAIL">Fail</Radio>
                    <Radio value="NA">N/A</Radio>
                  </Radio.Group>
                  <Input
                    placeholder="Remarks"
                    value={item.remarks}
                    onChange={(e) => updateChecklistItem(index, 'remarks', e.target.value)}
                  />
                </Card>
              ))
            )}
            <Button type="dashed" block onClick={addAdHocItem} style={{ marginBottom: 16 }}>
              + Add Item
            </Button>

            {(!selectedMIR.workflow?.status || selectedMIR.workflow?.status === 'CREATED') && (
              <>
                <Divider>Submit for Review</Divider>
                <Form.Item label="Reviewer (must be a different person)" style={{ marginBottom: 12 }}>
                  <Select
                    placeholder="Select reviewer"
                    value={reviewerId || undefined}
                    onChange={setReviewerId}
                    options={users.map((u: any) => ({ value: u.id, label: u.full_name }))}
                  />
                </Form.Item>
                <Form.Item label="Inspection evidence photos (required)" style={{ marginBottom: 12 }}>
                  <Upload
                    beforeUpload={(file) => { setReviewEvidence((prev) => [...prev, file]); return false; }}
                    onRemove={(file) => setReviewEvidence((prev) => prev.filter((f) => f.name !== file.name))}
                    multiple
                    accept="image/*"
                  >
                    <Button icon={<UploadOutlined />}>Upload Evidence Photos</Button>
                  </Upload>
                </Form.Item>
                <Button
                  type="primary"
                  style={{ background: '#1B2A4A' }}
                  loading={submittingForReview}
                  onClick={handleSubmitForReview}
                  block
                >
                  Submit for Review
                </Button>
              </>
            )}

            {selectedMIR.workflow?.status === 'UNDER_REVIEW' && (
              <>
                <Divider>Finalize Result</Divider>
                <Radio.Group value={overallResult} onChange={(e) => setOverallResult(e.target.value)} style={{ marginBottom: 12 }}>
                  <Space direction="vertical">
                    <Radio value="ACCEPTED">Accepted</Radio>
                    <Radio value="ACCEPTED_WITH_CONDITIONS">Accepted with Conditions</Radio>
                    <Radio value="REJECTED">Rejected</Radio>
                  </Space>
                </Radio.Group>

                {overallResult === 'REJECTED' && (
                  <TextArea
                    rows={3}
                    placeholder="Rejection reason (mandatory)"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    style={{ marginBottom: 12 }}
                  />
                )}

                <Form.Item label="Review decision evidence photos (required)" style={{ marginBottom: 12 }}>
                  <Upload
                    beforeUpload={(file) => { setResultEvidence((prev) => [...prev, file]); return false; }}
                    onRemove={(file) => setResultEvidence((prev) => prev.filter((f) => f.name !== file.name))}
                    multiple
                    accept="image/*"
                  >
                    <Button icon={<UploadOutlined />}>Upload Evidence Photos</Button>
                  </Upload>
                </Form.Item>

                <Button
                  type="primary"
                  style={{ background: '#1B2A4A' }}
                  loading={submittingResult}
                  onClick={handleSubmitResult}
                  block
                >
                  Submit Inspection Result
                </Button>
              </>
            )}

            {(selectedMIR.workflow?.status === 'CLOSED' || selectedMIR.workflow?.status === 'REJECTED') && (
              <>
                <Divider>Final Result</Divider>
                <Tag color={selectedMIR.overall_result === 'REJECTED' ? 'red' : 'green'} style={{ fontSize: 14, padding: '4px 12px' }}>
                  {selectedMIR.overall_result?.replace(/_/g, ' ')}
                </Tag>
                {selectedMIR.rejection_reason && (
                  <div style={{ marginTop: 12 }}>
                    <Text strong>Rejection Reason:</Text>
                    <div>{selectedMIR.rejection_reason}</div>
                  </div>
                )}
              </>
            )}

            <Divider>Photos</Divider>
            <StagePhotos attachments={selectedMIR.attachments} />

            <Divider>Timeline</Divider>
            <RecordTimeline recordId={selectedMIR.workflow?.object_id} projectId={selectedProject} />
          </>
        )}
      </Drawer>

      <Modal
        title="Raise MIR"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Create MIR"
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="material_type"
            label="Material Type"
            rules={[{ required: true, message: 'Material type is required' }]}
          >
            <AutoComplete
              placeholder="Type material type (e.g. Reinforcement Steel, OPC Cement)"
              onChange={handleMaterialTypeSelect}
              options={materialTypes.map(m => ({ value: m }))}
              filterOption={(inputValue, option) =>
                option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="supplier_name" label="Supplier" style={{ flex: 1 }}>
              <Input placeholder="Supplier name" />
            </Form.Item>
            <Form.Item name="batch_lot_number" label="Batch/Lot Number" style={{ flex: 1 }}>
              <Input placeholder="e.g. BATCH-2026-0091" />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="quantity" label="Quantity" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="quantity_unit" label="Unit" style={{ flex: 1 }}>
              <Input placeholder="e.g. bags, tonnes, nos" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default MIRPage;